import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, TagIcon, ChevronRightIcon, SendIcon, CheckIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, TextArea } from '../components/ui/Input';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';

// ─────────────────────────────────────────────────────────────
// FORM EMAIL — same as Contact page
// ─────────────────────────────────────────────────────────────
const FORM_EMAIL = 'helgi@helgig.com';

// ─────────────────────────────────────────────────────────────
// EVENT DATA
// Update this list to add/remove/modify scheduled events.
//
// priceIS / priceEN: shown on the card, e.g. "49.900 kr." or "Contact for pricing"
// status: 'open' | 'few-left' | 'full' | 'coming-soon'
// ─────────────────────────────────────────────────────────────
interface ScheduledEvent {
  id: string;
  titleIS: string;
  titleEN: string;
  dateIS: string;
  dateEN: string;
  timeIS: string;
  timeEN: string;
  locationIS: string;
  locationEN: string;
  formatIS: string;
  formatEN: string;
  priceIS: string;
  priceEN: string;
  spotsTotal: number | null;
  spotsLeft: number | null;
  status: 'open' | 'few-left' | 'full' | 'coming-soon';
  internalPath: string;
  // Skráningarsíða. Hnappurinn "Skrá mig" birtist þegar status er 'open' eða 'few-left'.
  signupPath?: string;
  tagIS: string;
  tagEN: string;
  accentColor: string;
  accentText: string;
  accentLight: string;
}
const events: ScheduledEvent[] = [{
  id: 'ftm-autumn-2026',
  titleIS: 'Stjórnandi í Fyrsta Sinn',
  titleEN: 'First-Time Manager',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '6 vikur · Ein lota á viku',
  formatEN: '6 weeks · One session per week',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/first-time-manager',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-coral',
  accentText: 'text-coral',
  accentLight: 'bg-coral/10'
}, {
  id: 'conflict-autumn-2026',
  titleIS: 'Betri samskipti og heilbrigðari ágreiningar',
  titleEN: 'Better conversations, healthier conflict',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Reykjavík (staðfest við skráningu)',
  locationEN: 'Reykjavík (venue confirmed on booking)',
  formatIS: 'Hálfur dagur eða heill dagur',
  formatEN: 'Half day or full day (taught in Icelandic)',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/conflict-coaching',
  signupPath: '/events/betri-samskipti',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-teal',
  accentText: 'text-teal',
  accentLight: 'bg-teal/10'
}, {
  id: 'meetings-autumn-2026',
  titleIS: 'Betri Fjarfundir',
  titleEN: 'Better Online Meetings',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '2 x 2 klst',
  formatEN: '2 x 2 hrs',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/better-online-meetings',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-blue-600',
  accentText: 'text-blue-600',
  accentLight: 'bg-blue-50'
}];

// ─────────────────────────────────────────────────────────────
// Registration modal / inline form
// ─────────────────────────────────────────────────────────────
interface RegFormProps {
  event: ScheduledEvent;
  isIS: boolean;
  onClose: () => void;
}
function RegistrationForm({
  event,
  isIS,
  onClose
}: RegFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const title = isIS ? event.titleIS : event.titleEN;
  const date = isIS ? event.dateIS : event.dateEN;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(p => ({
      ...p,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasError(false);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${FORM_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message || '—',
          _subject: `Course registration interest: ${title}`,
          course: title,
          date
        })
      });
      if (res.ok) {
        setIsSubmitted(true);
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-id="element-1173" data-mp="2bjox2s3zt7o">
      <Animate data-id="element-1174" data-mp="gajdybjjaa4d">
        <Card className="w-full max-w-md p-8 relative shadow-2xl" data-id="element-1175" data-mp="g7x6ebwn7um5">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-1176" data-mp="bc9cxkb4ofau">
            <XIcon size={20} data-id="element-1177" data-mp="395jhcj2h0r9a" />
          </button>

          {isSubmitted ? <div className="text-center py-6" data-id="element-1178" data-mp="3e32v7hv9b20p">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1179" data-mp="3e29y78m3nqvr">
                <CheckIcon size={28} strokeWidth={2.5} data-id="element-1180" data-mp="3ntebm3w4n3oh" />
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-1181" data-mp="3nvic0buthkk0">
                {isIS ? 'Skráning móttekin!' : 'Got it!'}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-1182" data-mp="ogc45n6fjy9">
                {isIS ? 'Ég kem til baka til þín þegar dagsetning er staðfest.' : "I'll be in touch as soon as the date is confirmed."}
              </p>
              <Button variant="outline" onClick={onClose} size="sm" data-id="element-1183" data-mp="22ifnss47o3bh">
                {isIS ? 'Loka' : 'Close'}
              </Button>
            </div> : <>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${event.accentLight} ${event.accentText}`} data-id="element-1184" data-mp="1zwy6pu09hsr5">
                {isIS ? event.tagIS : event.tagEN}
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-1" data-id="element-1185" data-mp="1q3r24bdcigs4">{title}</h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-1186" data-mp="1l8brdsamx0zx">
                {isIS ? 'Láttu mig vita og ég kem til baka þegar dagsetning er staðfest.' : 'Leave your details and I will contact you when the date is confirmed.'}
              </p>

              {hasError && <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm mb-4 border border-red-100" data-id="element-1187" data-mp="28vjr7upanhuk">
                  {isIS ? 'Eitthvað fór úrskeiðis. Sendu beint á ' : 'Something went wrong. Email directly: '}
                  <a href={`mailto:${FORM_EMAIL}`} className="underline font-bold" data-id="element-1188" data-mp="xhxxrxceiu38">{FORM_EMAIL}</a>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1189" data-mp="thxfg1m6lyqw">
                <Input label={isIS ? 'Nafn' : 'Name'} name="name" placeholder={isIS ? 'Jón Jónsson' : 'Jane Smith'} value={formData.name} onChange={handleChange} required data-id="element-1190" data-mp="2sirl5ohigi1o" />
                <Input label={isIS ? 'Netfang' : 'Email'} name="email" type="email" placeholder={isIS ? 'jon@example.com' : 'jane@example.com'} value={formData.email} onChange={handleChange} required data-id="element-1191" data-mp="2oj9armnbfpd3" />
                <TextArea label={isIS ? 'Spurningar eða athugasemdir (valfrjálst)' : 'Questions or comments (optional)'} name="message" rows={3} placeholder={isIS ? 'Hér má setja spurningar eða sérstakar óskir...' : 'Any questions or special requirements...'} value={formData.message} onChange={handleChange} data-id="element-1192" data-mp="37hlkpam1x39q" />
                <Button type="submit" className="w-full justify-center" size="lg" isLoading={isSubmitting} rightIcon={<SendIcon size={16} data-id="element-1194" data-mp="hxqzfxubnkoi" />} data-id="element-1193" data-mp="94npw0msrrz5">
                  {isIS ? 'Láta vita' : 'Notify me'}
                </Button>
              </form>
            </>}
        </Card>
      </Animate>
    </div>;
}

// ─────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────
function StatusBadge({
  event,
  isIS
}: {
  event: ScheduledEvent;
  isIS: boolean;
}) {
  const map = {
    open: {
      label: isIS ? 'Skráning opin' : 'Open',
      classes: 'bg-green-100 text-green-700'
    },
    'few-left': {
      label: isIS ? 'Fá pláss eftir' : 'Few spots left',
      classes: 'bg-amber-100 text-amber-700'
    },
    full: {
      label: isIS ? 'Fullskráð' : 'Full',
      classes: 'bg-red-100 text-red-700'
    },
    'coming-soon': {
      label: isIS ? 'Í vinnslu' : 'Coming soon',
      classes: 'bg-gray-100 text-gray-500'
    }
  };
  const s = map[event.status];
  return <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${s.classes}`} data-id="element-1195" data-mp="13v947y2nigr9">
      {s.label}
    </span>;
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export function Events() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const [activeRegistration, setActiveRegistration] = useState<ScheduledEvent | null>(null);
  return <PageTransition data-id="element-1196" data-mp="fyv8hhf3upfg">
      {/* Registration modal */}
      {activeRegistration && <RegistrationForm event={activeRegistration} isIS={isIS} onClose={() => setActiveRegistration(null)} data-id="element-1197" data-mp="2fb7h6d0y9mxr" />}

      <div className="pt-12 pb-24" data-id="element-1198" data-mp="2acwagb5ybsjr">
        {/* Header */}
        <section className="text-center max-w-4xl mx-auto px-4 mb-16 relative" data-id="element-1199" data-mp="2iu6lqwxeh19j">
          <DecorativeShape type="square" color="bg-coral/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1200" data-mp="2muqhbw2brwtk" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-24 h-24 -z-10" data-id="element-1201" data-mp="2rl6y9q5cu0ko" />
          <Animate data-id="element-1202" data-mp="2wl1nzqde8k45">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1203" data-mp="2wjgtu1uzr36t">
              {isIS ? 'Námskeið og vinnustofur' : 'Courses and workshops'}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-id="element-1204" data-mp="23wmjhxrw1k72">
              {isIS ? 'Næstu viðburðir' : 'Upcoming events'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1205" data-mp="2duadglho6no6">
              {isIS ? 'Námskeið og vinnustofur eru í undirbúningi fyrir haust 2026. Skráðu þig á biðlista og ég kem til baka þegar dagsetningar eru staðfestar.' : 'Courses and workshops are being scheduled for autumn 2026. Register your interest below and I will be in touch when dates are confirmed.'}
            </p>
          </Animate>
        </section>

        {/* Event cards */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8" data-id="element-1206" data-mp="2uol34fvju4ah">
          {events.map((event, i) => <Animate key={event.id} delay={i * 0.08} data-id="element-1207" data-mp="34ko6fp2508z0" data-mpi={i}>
              <Card className="p-0 overflow-hidden border-2 border-gray-100 transition-colors" data-id="element-1208" data-mp="34l6a36r4ppgy">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-1209" data-mp="2gvc1j1duen2i">
                  {/* Accent panel */}
                  <div className={`lg:col-span-3 ${event.accentLight} p-8 flex flex-col justify-center items-center text-center gap-3`} data-id="element-1210" data-mp="2btmmlani1p95">
                    <div className={`w-14 h-14 rounded-full ${event.accentColor} text-white flex items-center justify-center shadow-md`} data-id="element-1211" data-mp="2ltdhvejvw8aa">
                      <CalendarIcon size={24} data-id="element-1212" data-mp="1y2rkch7ia6ej" />
                    </div>
                    <div data-id="element-1213" data-mp="1t615fx7ftgup">
                      <p className={`font-bold text-base ${event.accentText} leading-snug`} data-id="element-1214" data-mp="1t4q19wa2y732">
                        {isIS ? event.dateIS : event.dateEN}
                      </p>
                      {(isIS ? event.timeIS : event.timeEN) && <p className="text-xs text-gray-500 mt-1.5 flex items-center justify-center gap-1" data-id="element-1215" data-mp="3uuovt2p4ywbf">
                          <ClockIcon size={11} data-id="element-1216" data-mp="3nkqqdcjxk5n" />
                          {isIS ? event.timeIS : event.timeEN}
                        </p>}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${event.accentLight} ${event.accentText}`} data-id="element-1217" data-mp="wcjb7h45cye4">
                      {isIS ? event.tagIS : event.tagEN}
                    </span>
                  </div>

                  {/* Content panel */}
                  <div className="lg:col-span-9 p-8 bg-white flex flex-col justify-between gap-5" data-id="element-1218" data-mp="mgop2c0psl6l">
                    <div data-id="element-1219" data-mp="335bzzuwk36qz">
                      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap" data-id="element-1220" data-mp="374ktqzrlj4mj">
                        <h2 className="text-2xl font-bold text-charcoal" data-id="element-1221" data-mp="3nbz3hvv66y5o">
                          {isIS ? event.titleIS : event.titleEN}
                        </h2>
                        <StatusBadge event={event} isIS={isIS} data-id="element-1222" data-mp="3ijxvwn8y5vlz" />
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500" data-id="element-1223" data-mp="3dmfjgmvq0y98">
                        <span className="flex items-center gap-1.5" data-id="element-1224" data-mp="38onuo1qdhs99">
                          <MapPinIcon size={13} className="flex-shrink-0" data-id="element-1225" data-mp="a5p9xy0xk32s" />
                          {isIS ? event.locationIS : event.locationEN}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-1226" data-mp="6zqw1xascky">
                          <TagIcon size={13} className="flex-shrink-0" data-id="element-1227" data-mp="9cf2wtnfj6g" />
                          {isIS ? event.formatIS : event.formatEN}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-5 border-t border-gray-100" data-id="element-1228" data-mp="2gvwwhlp4pthd">
                      {event.signupPath && (event.status === 'open' || event.status === 'few-left') ? <Link to={event.signupPath} data-id="element-1229" data-mp="10ghito494xp4">
                          <Button rightIcon={<ChevronRightIcon size={15} data-id="element-1231" data-mp="15esr3uewf058" />} data-id="element-1230" data-mp="15ej32cdez326">
                            {isIS ? 'Skrá mig' : 'Register'}
                          </Button>
                        </Link> : <button onClick={() => setActiveRegistration(event)} className="inline-flex" data-id="element-1232" data-mp="1f55c1nqmkvbw">
                        <Button variant="outline" rightIcon={<ChevronRightIcon size={15} data-id="element-1234" data-mp="1o63j6ewd1iyp" />} data-id="element-1233" data-mp="1o50ri4r7rchq">
                          {isIS ? 'Láta vita þegar skráning opnar' : 'Notify me when registration opens'}
                        </Button>
                      </button>}

                      <Link to={event.internalPath} data-id="element-1235" data-mp="3pyrr31jy8ukt">
                        <Button variant="outline" size="sm" data-id="element-1236" data-mp="3uwjizvklggp0">
                          {isIS ? 'Um námskeiðið' : 'About the course'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Card>
            </Animate>)}
        </section>

        {/* Payment note */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-1237" data-mp="162v23kp8i4o2">
          <Animate data-id="element-1238" data-mp="164plgukuxoq1">
            <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-5" data-id="element-1239" data-mp="1kzm481qaluhd">
              <div className="w-5 h-5 rounded-full bg-gray-300 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-1240" data-mp="1g1103r7x6qnn">i</div>
              <p className="text-sm text-gray-600" data-id="element-1241" data-mp="1treae08aahg5">
                {isIS ? 'Við staðfestingu dagsetningar færð þú tölvupóst með skráningarleiðbeiningum og greiðsluupplýsingum. Greiðsla fer fram með reikningi eða bankamillifærslu. Við erfiðleika, hafðu samband beint á ' : 'When dates are confirmed you will receive an email with registration instructions and payment details. Payment is by invoice or bank transfer. For any questions, contact me directly at '}
                <a href={`mailto:${FORM_EMAIL}`} className="text-teal font-medium hover:underline" data-id="element-1242" data-mp="2nbdt5xlf74st">{FORM_EMAIL}</a>
              </p>
            </div>
          </Animate>
        </section>

        {/* Custom / in-house CTA */}
        <section className="max-w-4xl mx-auto px-4 mt-16" data-id="element-1243" data-mp="2dfslpjxl1cbo">
          <Animate data-id="element-1244" data-mp="1zlsy4gbzsh36">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-1245" data-mp="1zl9m1g90wmx2">
              <h2 className="text-2xl font-bold mb-3" data-id="element-1246" data-mp="24em4f8n3zxhd">
                {isIS ? 'Ertu að leita að sérsniðinni þjálfun?' : 'Looking for something tailored?'}
              </h2>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto" data-id="element-1247" data-mp="1uo9l1jr16act">
                {isIS ? 'Ég hanna og held námskeið og vinnustofur innanhúss hjá fyrirtækjum - aðlagað að ykkar samhengi og þörfum.' : 'I design and run in-house courses and workshops for organisations - adapted to your context and needs.'}
              </p>
              <Link to="/contact" data-id="element-1248" data-mp="1w5zt3diih37p">
                <Button variant="secondary" rightIcon={<ChevronRightIcon size={16} data-id="element-1250" data-mp="1w3vpyxsfs88h" />} data-id="element-1249" data-mp="1w71fge4z9c4q">
                  {isIS ? 'Hafa samband' : 'Get in touch'}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}